import { css } from 'aphrodite';
import * as React from 'react';
import {
	ISuccessInboxThread,
	SuccessInboxClientHandoff,
	SuccessInboxEscalationTarget,
	useSuccessInboxEscalateMutation,
	useSuccessInboxEscalationDraftQuery,
} from '../../../../queries/Admin/SuccessInbox';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { Dialog, DialogBody, DialogFooter, DialogPopup, DialogTitle } from '../../../../web/components-v2/Dialog';
import { Label } from '../../../../web/components-v2/Label';
import { TabToggle } from '../../../../web/components-v2/TabToggle';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { styleSheet } from './styles';

const TARGET_LABELS: Record<SuccessInboxEscalationTarget, string> = {
	Support: 'Support',
	Website: 'Website',
	Ads: 'Ads',
};

const HANDOFF_LABELS: Record<SuccessInboxClientHandoff, string> = {
	TeamContactsClient: 'They contact the client',
	TeamRepliesToRep: 'They reply to me',
};

interface IProps {
	onClose: () => void;
	onSent: () => void;
	thread: ISuccessInboxThread;
}

export const SuccessInboxEscalateDialog: React.FC<IProps> = ({ onClose, onSent, thread }) => {
	const draftQuery = useSuccessInboxEscalationDraftQuery({ enabled: true, threadId: thread.id });

	const [target, setTarget] = React.useState<SuccessInboxEscalationTarget | null>(null);
	const [handoff, setHandoff] = React.useState<SuccessInboxClientHandoff | null>(null);
	const [notifyClient, setNotifyClient] = React.useState<boolean | null>(null);
	const [clientMessage, setClientMessage] = React.useState<string | null>(null);
	const [note, setNote] = React.useState('');
	const [error, setError] = React.useState<string | null>(null);
	const notifyCheckboxId = React.useId();

	const draft = draftQuery.data;

	// The drafted wording comes from the API so that what is shown here and what actually sends cannot
	// drift apart. Local state stays null until it arrives, then holds whatever the rep edited.
	const currentTarget = target ?? draft?.suggestedTarget ?? 'Support';
	const currentHandoff = handoff ?? draft?.suggestedHandoff ?? 'TeamContactsClient';
	const currentNotify = notifyClient ?? draft?.notifyClient ?? false;
	const currentClientMessage = clientMessage ?? draft?.clientMessage ?? '';

	const escalateMutation = useSuccessInboxEscalateMutation({
		onError: err => setError(err?.systemMessage || 'That did not send. Nothing has been emailed.'),
		onSuccess: () => onSent(),
	});

	const send = () => {
		setError(null);
		escalateMutation.mutate({
			id: thread.id,
			request: {
				clientMessage: currentNotify ? currentClientMessage.trim() : undefined,
				handoff: currentHandoff,
				notifyClient: currentNotify,
				note: note.trim() || undefined,
				target: currentTarget,
			},
		});
	};

	// Send stays shut until the draft lands. The button label reads the same either way, so an early
	// click would quietly send with the drafted client note missing.
	const canSend =
		!escalateMutation.isPending &&
		!draftQuery.isLoading &&
		!draftQuery.isError &&
		(!currentNotify || currentClientMessage.trim().length > 0);

	return (
		<Dialog onOpenChange={open => (open ? undefined : onClose())} open={true}>
			<DialogPopup>
				<DialogTitle>Send to another team</DialogTitle>
				<DialogBody>
					{draftQuery.isLoading ? (
						<div className={css(styleSheet.message)}>Preparing…</div>
					) : draftQuery.isError ? (
						<div className={css(styleSheet.warning)}>
							This escalation could not be prepared, so nothing has been sent.
						</div>
					) : (
						<div className={css(styleSheet.form)}>
							{draft && !draft.senderIsRep ? (
								<div className={css(styleSheet.hint)}>
									This is {draft.repName}&apos;s thread. Everything below goes out from you, copying them.
								</div>
							) : null}

							<div className={css(styleSheet.field)}>
								<Label>Send to</Label>
								<TabToggle
									items={(draft?.availableTargets ?? ['Support']).map(value => ({
										label: TARGET_LABELS[value],
										value,
									}))}
									onValueChange={value => setTarget(value as SuccessInboxEscalationTarget)}
									value={currentTarget}
								/>
								{draft?.suggestedTarget === currentTarget ? (
									<span className={css(styleSheet.hint)}>Suggested from how this thread was classified.</span>
								) : null}
							</div>

							<div className={css(styleSheet.field)}>
								<Label>Then</Label>
								<TabToggle
									items={(['TeamContactsClient', 'TeamRepliesToRep'] as SuccessInboxClientHandoff[]).map(value => ({
										label: HANDOFF_LABELS[value],
										value,
									}))}
									onValueChange={value => setHandoff(value as SuccessInboxClientHandoff)}
									value={currentHandoff}
								/>
								<span className={css(styleSheet.hint)}>
									{currentHandoff === 'TeamContactsClient'
										? `They will email ${thread.senderEmail} directly, copying you.`
										: 'They will reply to you, and you handle the client.'}
								</span>
							</div>

							<div className={css(styleSheet.field)}>
								<Label>Note for the team</Label>
								<TextArea
									onChange={event => setNote(event.target.value)}
									placeholder='Anything they should know? e.g. in meetings until 4, client has asked twice.'
									rows={2}
									value={note}
								/>
							</div>

							<div className={css(styleSheet.field)}>
								<div className={css(styleSheet.checkboxRow)}>
									<Checkbox
										checked={currentNotify}
										id={notifyCheckboxId}
										onCheckedChange={checked => setNotifyClient(checked === true)}
									/>
									<Label htmlFor={notifyCheckboxId}>Also reply to the client now</Label>
								</div>
								{currentNotify ? (
									<>
										<TextArea
											onChange={event => setClientMessage(event.target.value)}
											rows={4}
											value={currentClientMessage}
										/>
										<span className={css(styleSheet.hint)}>
											Sent from {draft?.sendingFromEmail} as a reply on the client&apos;s own thread, copying you.
										</span>
									</>
								) : null}
							</div>

							{error ? <div className={css(styleSheet.warning)}>{error}</div> : null}
						</div>
					)}
				</DialogBody>
				<DialogFooter>
					<Button onClick={onClose} size='sm' variant='ghost'>
						Cancel
					</Button>
					<Button disabled={!canSend} onClick={send} size='sm' variant='primary'>
						{escalateMutation.isPending ? 'Sending…' : `Send to ${TARGET_LABELS[currentTarget]}`}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
